import React, { useState } from 'react';
import { Check, Link, Loader2 } from 'lucide-react';
import { api } from '../services/api';

/** Only offer sharing for properties with a persisted render and a public slug. */
export function PresentationLink({ property }: { property: { id: number; slug: string | null; render_count: number } }) {
  const [status, setStatus] = useState<'idle' | 'pending' | 'ready' | 'copying' | 'copied' | 'publish-error' | 'copy-error'>('idle');
  if (!property.slug || property.render_count < 1) return null;

  const shareLink = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (status === 'pending' || status === 'copying') return;
    if (status === 'ready' || status === 'copy-error' || status === 'copied') {
      setStatus('copying');
      try {
        // Invoke the native clipboard API immediately in a fresh user click.
        // No network await may precede this call (Safari/Firefox activation).
        await navigator.clipboard.writeText(`${window.location.origin}/p/${property.slug}`);
        setStatus('copied');
      } catch {
        // Publishing already succeeded; retry copying without another request.
        setStatus('copy-error');
      }
      return;
    }
    setStatus('pending');
    try {
      // This app owns /p/:slug. Never copy before publishing succeeds.
      await api.patch(`/api/properties/${property.id}/public`, { isPublic: true });
      setStatus('ready');
    } catch {
      setStatus('publish-error');
    }
  };

  const busy = status === 'pending' || status === 'copying';
  return (
    <div className="inline-flex flex-col items-start gap-1">
      <button type="button" onClick={shareLink} disabled={busy}
        className="inline-flex items-center gap-1.5 px-2.5 py-2 text-xs font-semibold text-ink border border-black/10 rounded-lg hover:bg-stone transition-colors disabled:opacity-50">
        {busy ? <Loader2 size={13} className="animate-spin" /> : status === 'copied' ? <Check size={13} /> : <Link size={13} />}
        {status === 'pending' ? 'Link delen…' : status === 'copying' ? 'Link kopiëren…' : status === 'copied' ? 'Link gekopieerd' : status === 'ready' || status === 'copy-error' ? 'Kopieer presentatielink' : 'Presentatielink'}
      </button>
      {status === 'ready' && <span role="status" className="text-xs text-ink-muted">Link klaar. Klik om te kopiëren.</span>}
      {status === 'publish-error' && <span role="alert" className="text-xs text-red-600">Presentatielink delen mislukt. Probeer opnieuw.</span>}
      {status === 'copy-error' && <span role="alert" className="text-xs text-red-600">Link kopiëren mislukt. Klik opnieuw om te kopiëren.</span>}
    </div>
  );
}
